import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  RefreshCw, 
  AlertTriangle,
  Bot,
  ShieldAlert,
  TrendingUp,
  Users,
  Eye,
  MessageSquare
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const ADMIN_API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/secure-admin`;

export const AdminAIRiskPage = () => {
  const { token } = useAdminAuth();
  const [metrics, setMetrics] = useState(null);
  const [highRiskUsers, setHighRiskUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRisk, setUserRisk] = useState(null);

  const fetchRiskCenter = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${ADMIN_API_URL}/ai/risk-center`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load risk center');

      const data = await response.json();
      setMetrics(data.metrics);
      setHighRiskUsers(data.high_risk_users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRisk = async (userId) => {
    try {
      const response = await fetch(`${ADMIN_API_URL}/ai/user-risk/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load user risk');

      const data = await response.json();
      setUserRisk(data);
      setSelectedUser(userId);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchRiskCenter();
    // Refresh every 5 minutes
    const interval = setInterval(fetchRiskCenter, 300000);
    return () => clearInterval(interval);
  }, [token]);

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'bg-red-600/20 text-red-400 border-red-600';
      case 'medium': return 'bg-yellow-600/20 text-yellow-400 border-yellow-600';
      default: return 'bg-green-600/20 text-green-400 border-green-600';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bot className="w-6 h-6 text-purple-400" />
            AI Risk Center
          </h1>
          <p className="text-gray-400 text-sm">AI-powered pattern detection and risk assessment</p>
        </div>
        <Button onClick={fetchRiskCenter} variant="outline" size="sm" className="border-gray-600">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="bg-red-900/50 border-red-800">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Advisory Note */}
      <Alert className="bg-purple-900/30 border-purple-700">
        <Bot className="h-4 w-4 text-purple-400" />
        <AlertDescription className="text-purple-200">
          All AI assessments are advisory only. Human review and approval is required before any action.
        </AlertDescription>
      </Alert>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Anomalies Detected"
          value={metrics?.anomaly_count || 0}
          icon={ShieldAlert}
          color="red"
        />
        <MetricCard
          title="Pending Recommendations"
          value={metrics?.pending_recommendations || 0}
          icon={MessageSquare}
          color="yellow"
        />
        <MetricCard
          title="High Risk Users"
          value={metrics?.high_risk_user_count || 0}
          icon={Users}
          color="orange"
        />
        <MetricCard
          title="Last Updated"
          value={metrics?.last_updated ? new Date(metrics.last_updated).toLocaleTimeString() : 'N/A'}
          icon={TrendingUp}
          color="blue"
          isText
        />
      </div>

      {/* Anomalies */}
      {metrics?.anomalies?.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-400" />
              Platform Anomalies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {metrics.anomalies.map((anomaly, i) => (
              <div 
                key={i} 
                className={`p-4 rounded-lg border ${
                  anomaly.severity === 'high' 
                    ? 'bg-red-900/20 border-red-700' 
                    : 'bg-yellow-900/20 border-yellow-700'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`font-semibold ${
                      anomaly.severity === 'high' ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      {anomaly.type.replace(/_/g, ' ').toUpperCase()}
                    </p>
                    <p className="text-gray-300 text-sm mt-1">{anomaly.detail}</p>
                    <p className="text-gray-500 text-xs mt-2">{anomaly.recommendation}</p>
                  </div>
                  <Badge className={
                    anomaly.severity === 'high' 
                      ? 'bg-red-600/20 text-red-400' 
                      : 'bg-yellow-600/20 text-yellow-400'
                  }>
                    {anomaly.severity}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* High Risk Users */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-400" />
            High Risk Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          {highRiskUsers.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No high risk users detected</p>
          ) : (
            <div className="space-y-3">
              {highRiskUsers.map((user) => (
                <div 
                  key={user.user_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-700/50 hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">
                        {user.risk_score}
                      </span>
                    </div>
                    <div>
                      <p className="text-white">{user.email || user.name || 'Anonymous'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getRiskColor(user.risk_level)}>
                          {user.risk_level} risk
                        </Badge>
                        {user.top_signals?.slice(0, 2).map((signal, i) => (
                          <span key={i} className="text-xs text-gray-500">
                            {signal.signal}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => fetchUserRisk(user.user_id)}
                    className="text-gray-400 hover:text-white"
                    data-testid={`view-risk-${user.user_id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Risk Detail Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setUserRisk(null); }}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>User Risk Assessment</DialogTitle>
          </DialogHeader>
          {userRisk && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Risk Score</span>
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold">{userRisk.score}</span>
                  <Badge className={getRiskColor(userRisk.risk_level)}>
                    {userRisk.risk_level}
                  </Badge>
                </div>
              </div>

              {userRisk.signals?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-gray-400 text-sm">Risk Signals:</p>
                  {userRisk.signals.map((signal, i) => (
                    <div 
                      key={i}
                      className={`p-3 rounded border ${
                        signal.severity === 'high'
                          ? 'bg-red-900/20 border-red-700'
                          : 'bg-yellow-900/20 border-yellow-700'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-medium">{signal.signal}</p>
                          <p className="text-gray-400 text-sm">{signal.detail}</p>
                        </div>
                        <span className="text-sm text-gray-500">+{signal.score_impact}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Alert className="bg-purple-900/30 border-purple-700">
                <Bot className="h-4 w-4 text-purple-400" />
                <AlertDescription className="text-purple-200 text-xs">
                  {userRisk.advisory_note}
                </AlertDescription>
              </Alert>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setSelectedUser(null); setUserRisk(null); }}
              className="border-gray-600"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MetricCard = ({ title, value, icon: Icon, color, isText }) => {
  const colors = {
    red: 'bg-red-600',
    yellow: 'bg-yellow-600',
    orange: 'bg-orange-600',
    blue: 'bg-blue-600'
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm">{title}</p>
            <p className={`${isText ? 'text-lg' : 'text-2xl'} font-bold text-white mt-1`}>
              {isText ? value : value.toLocaleString()}
            </p>
          </div>
          <div className={`${colors[color]} p-2 rounded-lg`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminAIRiskPage;
